<template>
  <div class="relative mb-3">
    <div class="flex items-center gap-3 m-auto text-center">
      <Avatar class="w-16" :user-item="creator"/>

      <div class="flex flex-col items-start">
        <div class="flex items-center mb-1">
          <p class="h3 leading-5">{{ creator.name }}</p>

          <InlineSvg
            v-if="creator.tikTokFollowerCount"
            :src="require('@/assets/svg/design/verified.svg')"
            class="ml-0.5 shrink-0"/>
        </div>

        <p
          v-if="creator.username"
          class="leading-5 font-bold text-gray-500">
          @{{ creator.username }}
        </p>
      </div>
    </div>

    <button
      type="button"
      class="absolute right-0 top-0 btn py-2 pl-3 pr-0 cursor-pointer"
      @click="close">
      <ImageLoader v-slot="{ setLoaded }">
        <InlineSvg
          :src="crossSVG"
          class="text-gray-400"
          @loaded="setLoaded"/>
      </ImageLoader>
    </button>
  </div>

  <ul class="subscribe-list space-y-1 mb-6 text-[13px]">
    <li>
      <p v-html="$t('creator.subscribe.points.full_access', {name: creator.name})"/>
    </li>

    <li v-if="!secretToken">
      <p v-html="$t('creator.subscribe.points.direct_messages', {name: `<b>${creator.name}</b>`})"/>
    </li>

    <li v-if="!secretToken">
      <p v-html="$t('creator.subscribe.points.exclusive_chat', {name: `<b>${creator.name}</b>`})"/>
    </li>
  </ul>

  <!--<pre>{{ activeSub }}</pre>-->
  <!--<pre>{{ creator }}</pre>-->

  <!-- Subscription types -->
  <div
    v-if="(creator.subscriptionPlans?.length > 1) && !options.anySubscriptionForFree"
    :class="{
      'grid-cols-2 max-w-[280px] mx-auto': creator.subscriptionPlans.length === 2,
      'grid-cols-3': creator.subscriptionPlans.length === 3
    }"
    class="grid gap-2 mt-2 mb-3 justify-center">
    <div
      v-for="item in creator?.subscriptionPlans"
      :key="item.id"
      :class="{ 'ring-2 ring-inset ring-[#f23459]': item.id === activeSub.id }"
      class="relative h-22 rounded-lg subs-shadow flex flex-col justify-between"
      @click="activeSub = item">
      <span
        v-if="item.discountInPercents"
        class="absolute left-0 right-0 -top-2.5 w-max mx-auto text-[11px] leading-3 rounded-full bg-[#f23459] px-2 py-1 text-white font-semibold">
        {{ item.discountInPercents }}% OFF
      </span>

      <header class="pt-2.5 h-16 flex flex-col items-center justify-center rounded-t-lg">
        <h4 class="text-center text-[#808080] text-base font-bold leading-4">
          US${{ calculateItemPrice(item.priceInCurrency) }}
        </h4>

        <span class="text-caption">{{ getSubscriptionPeriodByLetter(item.period).per_period }}</span>
      </header>

      <footer
        :class="[item.id === activeSub.id ? 'text-white bg-[#f23459]' : 'text-black bg-[#ebebf2]']"
        class="rounded-b-lg h-6  flex items-center justify-center">
        <h4 class="text-center text-caption uppercase">{{ getSubscriptionPeriodByLetter(item.period).period }}</h4>
      </footer>
    </div>
  </div>

  <button
    v-if="!Number(creator.subscriptionPriceInCurrency) && !creator.subscriptionPlans?.length"
    type="submit"
    class="btn btn-block btn-primary relative h-[44px]"
    :disabled="isLoading"
    @click="freeSubscription">
    <Spinner v-if="isLoading" class="absolute inset-0 m-auto"/>

    <template v-else>{{ $t('creator.subscribe.for_free') }}</template>
  </button>

  <Form
    v-else
    id="cardForm"
    ref="cardForm"
    v-slot="{ errors, setErrors }"
    :initial-values="initialValues"
    @submit="paidSubscription">
    <div
      v-if="activeSub?.creditsProduct"
      class="flex gap-3 pt-1 mb-4">
      <div class="flex h-6 shrink-0 items-center">
        <div class="group grid size-4 grid-cols-1">
          <input
            id="purchase-credits"
            v-model="purchaseCredits"
            type="checkbox"
            name="purchase-credits"
            class="col-start-1 row-start-1 appearance-none rounded border-3 border-rose-600 bg-white checked:focus:bg-rose-600 checked:hover:bg-rose-600 checked:border-rose-600 checked:bg-rose-600 indeterminate:border-rose-600 indeterminate:bg-rose-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus:outline-blue-300 focus-visible:outline-blue-300 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto">
          <svg
            viewBox="0 0 14 14"
            fill="none"
            class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
            <path
              d="M3 8L6 11L11 3.5"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="opacity-0 group-has-checked:opacity-100"/>
            <path
              d="M3 7H11"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="opacity-0 group-has-indeterminate:opacity-100"/>
          </svg>
        </div>
      </div>

      <div class="text-[14px] leading-5">
        <label
          for="purchase-credits"
          class="font-medium text-gray-900"
          v-html="$t('credits.with_subscription', {
            cost: activeSub.creditsProduct.creditCount,
            price_in_currency: creditsPriceToShow
          })"/>
      </div>
    </div>

    <YupTooltip :i18n="errors.terms">
      <div class="flex items-start">
        <div class="h-5 flex items-center">
          <Field
            id="terms"
            type="checkbox"
            name="terms"
            class="mt-1.5 h-4 w-4 text-primary border border-primary rounded focus:ring-blue-300"
            :value="true"
            :rules="rules.agreeWithPolicy"
            @focus="setErrors(fieldsToReset)"/>
        </div>

        <div class="ml-3 text-sm">
          <label
            for="terms"
            class="font-medium text-gray-400 text-caption leading-4">
            <VRuntimeTemplate
              :template="$t('creator.subscribe.terms_agreement', { terms_of_service: termsOfService, privacy_policy: privacyPolicy })"/>
          </label>
        </div>
      </div>
    </YupTooltip>

    <YupTooltip :i18n="errors.recurring">
      <div class="flex items-start mb-4">
        <div class="h-5 flex items-center">
          <Field
            id="recurring"
            type="checkbox"
            name="recurring"
            class="mt-1.5 h-4 w-4 text-primary border border-primary rounded focus:ring-blue-300"
            :value="true"
            :rules="rules.agreeWithRecurring"
            @focus="setErrors(fieldsToReset)"/>
        </div>

        <div class="ml-3 text-sm">
          <label
            for="recurring"
            class="font-medium text-gray-400 text-caption leading-4">
            <VRuntimeTemplate
              v-if="!creator.subscriptionPlans?.length || activeSub.period === 'm'"
              :template="$t('creator.subscribe.recurring_agreement', {
                recurring_policy: recurringPolicy,
                date_day: currentDayOfMonth(),
                price: calculateItemPrice(priceInCurrencyWithoutCredits)
              })"/>

            <VRuntimeTemplate
              v-else-if="activeSub.period === 'w'"
              :template="$t('creator.subscribe.recurring_agreement_week', {
                recurring_policy: recurringPolicy,
                week_day: $dayjs().format('dddd'),
                price: calculateItemPrice(priceInCurrencyWithoutCredits)
              })"/>

            <VRuntimeTemplate
              v-else-if="activeSub.period === 'y'"
              :template="$t('creator.subscribe.recurring_agreement_year', {
                recurring_policy: recurringPolicy,
                date_day: currentDayOfMonth(),
                month: $dayjs().format('MMMM'),
                price: calculateItemPrice(priceInCurrencyWithoutCredits)
              })"/>
          </label>
        </div>
      </div>
    </YupTooltip>

    <template v-if="!isLoading">
      <AlertMessage
        v-if="isPending"
        type="warning"
        class="mb-2.5"
        heading="Transaction pending, check later or contact online support"/>

      <AlertMessage
        v-else-if="isFailed || errorMessage"
        type="error"
        class="mb-3"
        :heading="errorMessage || 'Previous transaction failed'"
        :text="errorDescription"/>

      <AlertMessage
        v-else-if="showWarnUpdatingCard"
        type="info"
        class="mb-3"
        heading="When making a payment, you will have to update your card details once more!"/>
    </template>

    <AppPaymentEmailForm
      v-if="!user.email"
      ref="emailForm"
      class="my-4"/>

    <!-- Special case for users who allowed for any subscriptions for free -->
    <button
      v-if="options.anySubscriptionForFree"
      type="button"
      class="btn btn-block btn-primary relative h-[44px]"
      :disabled="isLoading || isPending"
      @click="freeSubscription">
      <Spinner
        v-show="isLoading"
        class="absolute inset-0 m-auto"/>

      <span v-show="!isLoading">
        {{ Number(priceInCurrencyWithCredits) ? $t('creator.subscribe.for_price', { price: priceToShow }) : $t('creator.subscribe.for_free') }}
      </span>
    </button>

    <button
      v-else-if="Number(creator.subscriptionPriceInCurrency) || creator.subscriptionPlans?.length"
      class="btn btn-block btn-primary relative h-[44px]"
      :disabled="isLoading || isPending">
      <Spinner
        v-show="isLoading"
        class="absolute inset-0 m-auto"/>

      <span v-show="!isLoading">
        {{ $t('creator.subscribe.for_price', { price: priceToShow }) }}
      </span>
    </button>

    <button
      v-if="paymentMethods.length"
      type="button"
      class="btn btn-block btn-secondary mt-3"
      :disabled="isLoading"
      @click="payWithNewCard">
      {{ $t('pay_with_new_card') }}
    </button>

    <p
      v-if="priceWithTaxNote"
      class="text-xs text-gray-600 text-right mt-1 mb-2">
      {{ priceWithTaxNote }}
    </p>
  </Form>

  <p class="text-sm text-gray-400 text-center mt-2">
    {{ $t('creator.subscribe.cancel_any_time') }}
  </p>
</template>

<script>
import Avatar from '@/components/Functional/Avatar'
import ImageLoader from '@/components/Functional/ImageLoader'
import crossSVG from '@/assets/svg/design/cross.svg'
import app from '@/main'
import { mapActions, mapState, mapWritableState } from 'pinia'
import { useUserStore } from '@/stores/user'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'
import { useSubscriptionsStore } from '@/stores/subscriptions'
import YupTooltip from '@/components/Functional/YupTooltip'
import { Field, Form } from 'vee-validate'
import { usePaymentStore } from '@/stores/payment'
import AlertMessage from '@/components/Functional/AlertMessage.vue'
import Spinner from '@/components/Functional/Spinner.vue'
import uniqid from 'uniqid'
import AppPaymentEmailForm from '@/components/App/AppPaymentEmailForm.vue'
import { calcPriceToShow, calcPriceWithTaxNote, calcExpectedPriceInCents } from '@/helpers/tax-helpers.js'

export default {
  name: 'SubscribeContent',
  components: {
    Avatar,
    ImageLoader,
    Field,
    Form,
    YupTooltip,
    AlertMessage,
    Spinner,
    AppPaymentEmailForm
  },
  props: {
    creator: {
      type: Object,
      default: () => ({})
    },
    isVisible: {
      type: Boolean,
      default: false
    },
    close: {
      type: Function,
      default: null
    }
  },
  emits: [
    'subscribed',
    'modal-closed'
  ],
  data () {
    return {
      purchaseCredits: false,
      activeSub: null,
      payment: null,
      errorMessage: null,
      errorDescription: null,
      successUrl: null,
      crossSVG: crossSVG,
      isLoading: false,
      initialValues: {
        agreeWithPolicy: false,
        agreeWithRecurring: false
      },
      fieldsToReset: {
        agreeWithPolicy: false,
        agreeWithRecurring: false
      },
      rules: {
        agreeWithRecurring: this.$yup.boolean()
          .required({ key: 'yup.custom.terms_and_conditions', values: {} })
          .oneOf([true], () => ({ key: 'yup.custom.terms_and_conditions', values: {} })),
        agreeWithPolicy: this.$yup.boolean()
          .required({ key: 'yup.custom.terms_and_conditions', values: {} })
          .oneOf([true], () => ({ key: 'yup.custom.terms_and_conditions', values: {} }))
      }
    }
  },
  computed: {
    ...mapState(useAppStore, [
      'secretToken',
      'currentTime',
      'endlessPaymentCreated'
    ]),
    ...mapState(useUserStore, [
      'balance',
      'premiumSupportAgentUserId',
      'user'
    ]),
    ...mapState(useAuthStore, [
      'options'
    ]),
    ...mapState(usePaymentStore, [
      'activePaymentMethod',
      'paymentMethods',
      'subscriptionPayments',
      'showWarnUpdatingCard'
    ]),
    ...mapWritableState(usePaymentStore, [
      'pathBeforeBuying'
    ]),
    ...mapWritableState(useSubscriptionsStore, [
      'recentSubscriptions'
    ]),
    subscriptionPayment () {
      return this.subscriptionPayments.find(item => item.id.includes('subscription_' + this.creator.userId))
    },
    isPending () {
      return this.subscriptionPayment && this.subscriptionPayment && ['created', 'payment_pending', 'payment_accepted'].includes(this.subscriptionPayment.status)
    },
    isFailed () {
      return this.subscriptionPayment && ['failed', 'cancelled'].includes(this.subscriptionPayment.status)
    },
    priceInCurrency () {
      if (this.activeSub) {
        if (this.purchaseCredits && this.activeSub?.creditsProduct) {
          return (Number(this.activeSub.priceInCurrency) + Number(this.activeSub.creditsProduct.priceInCurrency)).toFixed(2)
        }
        else {
          return this.activeSub.priceInCurrency
        }
      }
      else {
        return this.creator.subscriptionPriceInCurrency
      }
    },
    priceInCurrencyWithoutCredits () {
      if (this.activeSub) {
        if (this.purchaseCredits && this.activeSub?.creditsProduct) {
          return Number(this.activeSub.priceInCurrency)
        }
        else {
          return this.activeSub.priceInCurrency
        }
      }
      else {
        return this.creator.subscriptionPriceInCurrency
      }
    },
    termsOfService () {
      return `<a
                href="${window.one2fan.origin}/terms"
                target="_blank"
                class="text-primary">
                {{ $t("common.terms_of_service") }}
              </a>`
    },
    privacyPolicy () {
      return `<a
                href="${window.one2fan.origin}/policy"
                target="_blank"
                class="text-primary">
                {{ $t('common.privacy_policy') }}
              </a>`
    },
    recurringPolicy () {
      return `<a
                href="${window.one2fan.origin}/recurring-policy"
                target="_blank"
                class="text-primary">
                {{ $t('common.recurring_policy') }}
              </a>`
    },
    priceToShow () {
      return calcPriceToShow(this.priceInCurrency)
    },
    creditsPriceToShow () {
      return calcPriceToShow(this.activeSub?.creditsProduct.priceInCurrency, false)
    },
    priceWithTaxNote () {
      return calcPriceWithTaxNote(this.$t, this.priceInCurrency)
    },
    expectedPriceInCents () {
      return calcExpectedPriceInCents(this.priceInCurrency)
    }
  },
  watch: {
    activeSub (value) {
      this.purchaseCredits = !!value.creditsProduct
    },
    subscriptionPayment (payment) {
      if (payment?.status === 'succeeded') {
        this.errorMessage = null
        this.errorDescription = null

        this.purchaseDelete(payment.purchaseId)
        this.close()
        this.$emit('modal-closed')

        if (this.successUrl && this.isVisible) {
          this.$router.push({ redirect: window.location.href = this.successUrl })
        }
      }
      else if (['failed', 'cancelled'].includes(payment?.status)) {
        this.errorMessage = payment?.error || 'Payment failed'
        this.purchaseDelete(payment.purchaseId)
      }
    }
  },
  async mounted () {
    if (this.subscriptionPayment) {
      const purchaseId = this.subscriptionPayment.purchaseId
      await this.purchaseGet(purchaseId)

      if (this.subscriptionPayment.status === 'created') {
        await this.purchaseCancel(purchaseId)
          .then(() => {
            this.purchaseDelete(purchaseId)
          })
      }
    }

    if (this.creator.subscriptionPlans?.length) {
      await this.$nextTick()
      this.activeSub = this.creator.subscriptionPlans[0]
      this.purchaseCredits = !!this.activeSub?.creditsProduct
    }
  },
  methods: {
    ...mapActions(usePaymentStore, [
      'checkoutSessionCreate',
      'purchase',
      'purchaseGet',
      'purchaseCreate',
      'purchaseDelete',
      'purchaseCancel'
    ]),
    calculateItemPrice (priceInCurrency) {
      return calcPriceToShow(priceInCurrency, false)
    },
    getSubscriptionPeriodByLetter (letter) {
      const periods = {
        w: {
          period: this.$t('common.weekly'),
          per_period: this.$t('subscriptions.period.per_week')
        },
        m: {
          period: this.$t('common.monthly'),
          per_period: this.$t('subscriptions.period.per_month')
        },
        y: {
          period: this.$t('common.annually'),
          per_period: this.$t('subscriptions.period.per_year')
        }
      }

      return periods[letter]
    },
    ordinalSuffixOf (i) {
      const j = i % 10
      const k = i % 100

      if (j === 1 && k !== 11) {
        return i + 'st'
      }
      if (j === 2 && k !== 12) {
        return i + 'nd'
      }
      if (j === 3 && k !== 13) {
        return i + 'rd'
      }
      return i + 'th'
    },
    currentDayOfMonth () {
      return this.ordinalSuffixOf(this.$dayjs().locale(this.$i18n.locale).format('D'))
    },
    async paidSubscription () {
      if (this.isPending || this.isLoading) {
        return
      }

      // 1. Есть сохраненная: карта stripe.purchase
      // 2. Нет сохраненной карты: stripe.purchase

      this.isLoading = true
      this.pathBeforeBuying = '/@' + this.creator.username

      // Check email
      if (!this.user.email) {
        const hasEmail = await this.$refs.emailForm.checkEmail()

        if (!hasEmail) {
          this.isLoading = false
          return
        }
      }

      // Приоритет подписок: 1 Выбранный план (первый по-умолчанию) 2 Старый способ месячной подписки

      let productId = this.creator.subscriptionPlans?.length ?
        `${this.creator.userId}:${this.activeSub.id}` : this.creator.userId

      if (this.activeSub?.idWithCredits && this.purchaseCredits) {
        productId = `${this.creator.userId}:${this.activeSub.idWithCredits}`
      }

      if (this.paymentMethods.length) {
        // Remove failed purchase if payment initialized again
        if (['failed', 'cancelled'].includes(this.subscriptionPayment?.status)) {
          this.purchaseDelete(this.subscriptionPayment.purchaseId)
        }

        // Check payment status or init one
        if (this.subscriptionPayment) {
          await this.purchaseGet(this.subscriptionPayment.purchaseId)
        }
        else {
          await this.purchaseCreate({ id: `subscription_${this.creator.userId}_${uniqid()}` })
        }

        if (this.endlessPaymentCreated) {
          await new Promise(resolve => setTimeout(resolve, 1000 * 5))
          this.isLoading = false
          return
        }

        this.successUrl = `${window.origin}/checkout/result?is=success&type=subscription&userId=${this.creator.userId}`

        await this.purchase({
          productType: 'subscription',
          productId: productId,
          paymentMethodId: this.activePaymentMethod.id,
          successUrl: this.successUrl,
          purchaseId: this.subscriptionPayment.purchaseId,
          expectedPriceInCents: this.expectedPriceInCents
        })
          .then(() => {
            this.errorMessage = null
            this.errorDescription = null
            this.close()
            this.$emit('modal-closed')
            this.isLoading = false

            if (process.env.NODE_ENV === 'production' && process.env.VUE_APP_IS_DEBUG !== 'yes') {
              // eslint-disable-next-line no-undef
              gtag('event', 'Submit', {
                event_category: 'Form',
                event_label: 'Subscription'
              })
            }
          })
          .catch((error) => {
            if (error?.code === 147) {
              this.errorMessage = error?.message
              this.errorDescription = error?.description
            }
            // Payment is pending
            else if (error?.code === 149) {
              //
            }
            else if (error?.code === 151) {
              this.errorMessage = 'Residence tax error, please refresh the page and try again'
            }
            else {
              this.errorMessage = 'Error occured'
            }

            // Remove payment
            if ([147, 105, 151].includes(error?.code)) {
              this.purchaseDelete(this.subscriptionPayment.purchaseId)
            }
          })
          .finally(async () => {
            if (this.subscriptionPayment) {
              await this.purchaseGet(this.subscriptionPayment.purchaseId)

              // If purchase not completed -> wait n seconds to show loading
              if (!['succeeded', 'failed', 'cancelled'].includes(this.subscriptionPayment?.status)) {
                await new Promise(resolve => setTimeout(resolve, 1000 * 10))
              }
            }

            this.isLoading = false
          })
      }
      else {
        await this.payWithNewCard()
      }
    },
    async payWithNewCard () {
      let productId = this.creator.subscriptionPlans?.length ?
        `${this.creator.userId}:${this.activeSub.id}` : this.creator.userId

      if (this.activeSub?.idWithCredits && this.purchaseCredits) {
        productId = `${this.creator.userId}:${this.activeSub.idWithCredits}`
      }

      if (!productId) {
        return
      }

      this.isLoading = true
      this.pathBeforeBuying = '/@' + this.creator.username

      const { valid } = await this.$refs.cardForm.validate()

      if (!valid) {
        this.isLoading = false
        return
      }

      try {
        await this.checkoutSessionCreate({
          productType: 'subscription',
          productId: productId,
          successUrl: `${window.origin}/checkout/result?is=success&type=subscription&userId=${this.creator.userId}`,
          cancelUrl: `${window.origin}/checkout/result?is=cancel&type=subscription&userId=${this.creator.userId}`,
          savePaymentMethod: true,
          expectedPriceInCents: this.expectedPriceInCents
        })
          .then(() => {
            this.errorMessage = null
            this.close()
            this.$emit('modal-closed')
          })
          .finally(() => {
            this.isLoading = false
          })
      } catch (error) {
        this.isLoading = false

        if (error.code === 151) {
          this.errorMessage = 'Residence tax error, please refresh the page and try again'
        }
        else {
          this.errorMessage = 'An error occurred while creating the checkout session'
        }
      }
    },
    async freeSubscription () {
      this.isLoading = true

      const res = await app.wsp.sendRequest({
        data: {
          publisherUserId: this.creator.userId
        },
        method: 'subscription.create'
      })

      this.isLoading = false

      if (res.error) {
        console.log(res.error.message)
      }

      if (process.env.NODE_ENV === 'production' && process.env.VUE_APP_IS_DEBUG !== 'yes') {
        // eslint-disable-next-line no-undef
        gtag('event', 'Submit', {
          event_category: 'Form',
          event_label: 'Subscription'
        })
      }

      this.$addEvent(['Subscription'], { userId: this.creator.userId })

      const userId = this.creator.userId + ''

      setTimeout(() => {
        if (!this.recentSubscriptions.includes(userId)) {
          this.recentSubscriptions.push(userId)
        }
      }, 5000)

      const user = await this.userGet(userId)
      this.$emit('subscribed', user)

      this.close()
      this.$emit('modal-closed')
    },
    async userGet (userId) {
      const res = await app.wsp.sendRequest({ data: { id: userId }, method: 'user.get' })

      if (res.error) {
        console.log(res.error.message)
      }

      return res.data.user
    }
  }
}
</script>

<style>
  .subscribe-list li:before {
    width: 13px;
    height: 18px;
    content: url('../../assets/svg/solid/check.svg');
    @apply mr-2
  }

  .subscribe-list li {
    display: flex;
  }

  .dating-heading b {
    @apply text-primary
  }

  .subs-shadow {
    box-shadow: rgba(99, 99, 99, 0.2) 0 2px 8px 0;
  }
</style>

